import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import {
  LIQUIDATION_BATCHES_URL,
  LIQUIDATIONS_BATCH_URL,
} from "constants/urls";
import { dateISOFormat } from "helpers";
import { utcFormatter } from "helpers/utcFormatter";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Client,
  ClientSettlementType,
  IContainerAggregation,
  Liquidation,
  LiquidationBatch,
  Movement,
  Store,
} from "types";
import { DATE_VIEWS, ROUTES, STATUS_CODES } from "../../../constants";
import LiquidationsTypesSelectors from "./LiquidationsTypesSelectors";
import AdditionalsModal from "./AdditionalsModal";
import { BATCH_POLLING_INTERVAL_MS } from "constants/index";

export type SelectedLiquidationTypes = {
  income: {
    calculation: string;
    minimum_value: string;
    description: string;
  };
  stay: {
    calculation: string;
    minimum_value: string;
    description: string;
  };
  discharge: {
    calculation: string;
    minimum_value: string;
    description: string;
  };
};

const selectedLiquidationTypesDefaultValue: SelectedLiquidationTypes = {
  income: {
    calculation: "",
    minimum_value: "0",
    description: "",
  },
  stay: {
    calculation: "",
    minimum_value: "0",
    description: "",
  },
  discharge: {
    calculation: "",
    minimum_value: "0",
    description: "",
  },
};

const getSelectedLiquidationTypesDefaultValue =
  (): SelectedLiquidationTypes => ({
    income: {
      calculation: "",
      minimum_value: "0",
      description: "",
    },
    stay: {
      calculation: "",
      minimum_value: "0",
      description: "",
    },
    discharge: {
      calculation: "",
      minimum_value: "0",
      description: "",
    },
  });

const buildZeroSettlementTypes = (
  settlementTypes: SelectedLiquidationTypes,
  fallbackTypes?: SelectedLiquidationTypes
): SelectedLiquidationTypes => {
  return {
    income: {
      ...settlementTypes.income,
      description:
        settlementTypes.income.description ||
        fallbackTypes?.income.description ||
        "pallets",
      calculation: "0",
    },
    stay: {
      ...settlementTypes.stay,
      description:
        settlementTypes.stay.description ||
        fallbackTypes?.stay.description ||
        "pallets",
      calculation: "0",
    },
    discharge: {
      ...settlementTypes.discharge,
      description:
        settlementTypes.discharge.description ||
        fallbackTypes?.discharge.description ||
        "pallets",
      calculation: "0",
    },
  };
};

export type SelectedLiquidationTypesByClient = Record<
  number,
  SelectedLiquidationTypes
>;

type Props = {
  clients: Client[];
  stores: Store[];
  liquidation: Liquidation;
  handleChange: <K extends keyof Liquidation>(
    name: K,
    value: Liquidation[K]
  ) => void;
  resetLiquidation: () => void;
};

export default function LiquidationCreate({
  clients,
  stores,
  liquidation,
  handleChange,
  resetLiquidation,
}: Props) {
  const {
    loading: liquidationLoading,
    createData: createLiquidationBatch,
  } = useCrud(LIQUIDATIONS_BATCH_URL, { disableAutoFetch: true });
  const { fetchData: fetchLiquidationBatch } = useCrud<LiquidationBatch>(
    LIQUIDATION_BATCHES_URL,
    { disableAutoFetch: true },
  );

  const { showSnackBar } = useToast();
  const navigate = useNavigate();
  const batchPollingTimeoutRef = useRef<number | null>(null);

  const [selectedLiquidationTypes, setSelectedLiquidationTypes] =
    useState<SelectedLiquidationTypes>(selectedLiquidationTypesDefaultValue);

  const [selectedLiquidationTypesByClient, setSelectedLiquidationTypesByClient] =
    useState<SelectedLiquidationTypesByClient>({});

  const [liquidationTypesLoading, setLiquidationTypesLoading] =
    useState<boolean>(false);
  const [liquidationBatchProcessing, setLiquidationBatchProcessing] =
    useState<boolean>(false);

  const [selectedChildClientIds, setSelectedChildClientIds] = useState<number[]>([]);

  const resetLiquidationTypes = useCallback(() => {
    setSelectedLiquidationTypes(selectedLiquidationTypesDefaultValue);
    setSelectedLiquidationTypesByClient({});
  }, []);

  const [additionalsModalOpen, setAdditionalsModalOpen] =
    useState<boolean>(false);

  const [extraMovement, setExtraMovement] = useState<Movement | undefined>();

  const handleMovementChange = (movement: Movement) => {
    setExtraMovement(movement);
  };

  const clearBatchPolling = useCallback(() => {
    if (batchPollingTimeoutRef.current) {
      window.clearTimeout(batchPollingTimeoutRef.current);
      batchPollingTimeoutRef.current = null;
    }
  }, []);

  const handleFailedBatch = useCallback(
    (batch: LiquidationBatch) => {
      clearBatchPolling();
      setLiquidationBatchProcessing(false);
      showSnackBar(
        batch.result?.message ?? "Ocurrio un error al crear la liquidacion.",
        "error",
      );
    },
    [clearBatchPolling, showSnackBar],
  );

  const handleSuccessfulBatch = useCallback(
    (batch: LiquidationBatch) => {
      const liquidationId =
        batch.result?.liquidation_id ?? batch.liquidation_ids[0];

      clearBatchPolling();
      setLiquidationBatchProcessing(false);

      if (!liquidationId) {
        showSnackBar(
          "La liquidacion finalizo, pero no se pudo obtener el detalle creado.",
          "error",
        );
        return;
      }

      resetLiquidation();
      resetLiquidationTypes();
      showSnackBar(
        batch.result?.message ?? "Liquidacion creada correctamente.",
        "success",
      );
      navigate(`${ROUTES.SALES}/${liquidationId}`);
    },
    [clearBatchPolling, navigate, resetLiquidation, resetLiquidationTypes, showSnackBar],
  );

  const pollLiquidationBatch = useCallback(
    async (batchId: number): Promise<void> => {
      const batchResponse = await fetchLiquidationBatch(`${batchId}`, true);

      if (batchResponse.status !== STATUS_CODES.OK) {
        clearBatchPolling();
        setLiquidationBatchProcessing(false);
        showSnackBar(
          batchResponse.data?.message ??
            "No se pudo consultar el estado de la liquidacion.",
          "error",
        );
        return;
      }

      const batch = batchResponse.data as LiquidationBatch;

      if (batch.status === "pending" || batch.status === "processing") {
        batchPollingTimeoutRef.current = window.setTimeout(() => {
          void pollLiquidationBatch(batchId);
        }, BATCH_POLLING_INTERVAL_MS);
        return;
      }

      if (batch.status === "success") {
        handleSuccessfulBatch(batch);
        return;
      }

      handleFailedBatch(batch);
    },
    [
      clearBatchPolling,
      fetchLiquidationBatch,
      handleFailedBatch,
      handleSuccessfulBatch,
      showSnackBar,
    ],
  );

  const clientLiquidationTypes = useMemo(() => {
    if (liquidation.client_id) {
      const client = clients.find((c) => c.id === liquidation.client_id);
      return client?.client_settlement_types as ClientSettlementType[];
    }
    return null;
  }, [clients, liquidation.client_id]);

  const selectedClient = useMemo(() => {
    if (!liquidation.client_id) return null;
    return clients.find((c) => c.id === liquidation.client_id) ?? null;
  }, [clients, liquidation.client_id]);

  const childClients = useMemo(() => {
    if (!selectedClient) return [];
    return clients.filter(
      (c) => c.parent_client_id === selectedClient.id && c.is_active
    );
  }, [clients, selectedClient]);

  const selectedChildClients = useMemo(
    () => childClients.filter((client) => selectedChildClientIds.includes(client.id)),
    [childClients, selectedChildClientIds]
  );

  const clientsToLiquidate = useMemo(() => {
    if (!selectedClient) return [];

    if (childClients.length > 0) {
      return [selectedClient, ...selectedChildClients];
    }

    return [selectedClient];
  }, [selectedClient, childClients.length, selectedChildClients]);

  const clientsToRender = useMemo(() => {
    if (!selectedClient) return [];

    if (childClients.length > 0) {
      return selectedChildClients;
    }

    return [selectedClient];
  }, [selectedClient, childClients.length, selectedChildClients]);

  const clientDefaultLiquidationTypes = () => {
    if (clientLiquidationTypes) {
      const defaultTypes = clientLiquidationTypes.filter(
        (liqType) => liqType.is_default
      );
      if (defaultTypes.length === 3) {
        //esta validacion en un futuro se puede volar, ahora lo pongo ya que hay algunos clientes deprecados que no tienen los 3 defaults
        const ingreso = defaultTypes.find(
          (liqType) => liqType.movement_type === "INGRESO"
        );

        const estadia = defaultTypes.find(
          (liqType) => liqType.movement_type === "ESTADIA"
        );

        const egreso = defaultTypes.find(
          (liqType) => liqType.movement_type === "EGRESO"
        );

        if (!ingreso || !estadia || !egreso) {
          return;
        }
        setLiquidationTypesLoading(true);
        setSelectedLiquidationTypes({
          income: {
            calculation: ingreso.price.toString(),
            minimum_value: (ingreso.minimum_value ?? 0).toString(),
            description: ingreso.settlement_type.description,
          },
          stay: {
            calculation: estadia.price.toString(),
            minimum_value: (estadia.minimum_value ?? 0).toString(),
            description: estadia.settlement_type.description,
          },
          discharge: {
            calculation: egreso.price.toString(),
            minimum_value: (egreso.minimum_value ?? 0).toString(),
            description: egreso.settlement_type.description,
          },
        });
        setLiquidationTypesLoading(false);
      } else {
        resetLiquidationTypes(); //en un futuro volarlo como el if de arriba
      }
    } else {
      resetLiquidationTypes();
    }
  };

  const buildDefaultLiquidationTypesFromClient = (
    clientId: number
  ): SelectedLiquidationTypes => {
    const client = clients.find((c) => c.id === clientId);
    const liquidationTypes =
      client?.client_settlement_types as ClientSettlementType[] | undefined;

    if (!liquidationTypes) return getSelectedLiquidationTypesDefaultValue();

    const defaultTypes = liquidationTypes.filter((liqType) => liqType.is_default);

    if (defaultTypes.length !== 3) {
      return getSelectedLiquidationTypesDefaultValue();
    }

    const ingreso = defaultTypes.find(
      (liqType) => liqType.movement_type === "INGRESO"
    );
    const estadia = defaultTypes.find(
      (liqType) => liqType.movement_type === "ESTADIA"
    );
    const egreso = defaultTypes.find(
      (liqType) => liqType.movement_type === "EGRESO"
    );

    if (!ingreso || !estadia || !egreso) {
      return getSelectedLiquidationTypesDefaultValue();
    }

    return {
      income: {
        calculation: ingreso.price.toString(),
        minimum_value: (ingreso.minimum_value ?? 0).toString(),
        description: ingreso.settlement_type.description,
      },
      stay: {
        calculation: estadia.price.toString(),
        minimum_value: (estadia.minimum_value ?? 0).toString(),
        description: estadia.settlement_type.description,
      },
      discharge: {
        calculation: egreso.price.toString(),
        minimum_value: (egreso.minimum_value ?? 0).toString(),
        description: egreso.settlement_type.description,
      },
    };
  };

  const resolveParentFallbackTypes = (): SelectedLiquidationTypes | undefined => {
    const firstChild = clientsToRender[0];

    if (!firstChild) {
      return undefined;
    }

    return (
      selectedLiquidationTypesByClient[firstChild.id] ??
      buildDefaultLiquidationTypesFromClient(firstChild.id)
    );
  };

  const storeOptions = useMemo(() => {
    return [
      { id: null, label: "Todos" },
      ...stores
        .filter((s) => s.is_active)
        .map((s) => ({
          id: s.id,
          label: s.description,
        })),
    ];
  }, [stores]);

  const onAdditionalItemChange = (additional_item: {
    id: number;
    description: string;
    price: number;
  }) => {
    if (
      !liquidation.additional_items?.some((ai) => ai.id === additional_item.id)
    ) {
      liquidation.additional_items?.push(additional_item);
    } else {
      liquidation.additional_items?.forEach((ai, index) => {
        if (ai.id === additional_item.id) {
          ai.description = additional_item.description;
          ai.price = additional_item.price;
        }
      });
    }
  };

  const handleCreateLiquidation = async () => {
    const hasChildren = clientsToLiquidate.length > 1;
    const parentFallbackTypes = resolveParentFallbackTypes();
    
    const liquidationExtraMovement =
      extraMovement &&
      (extraMovement.articles.length > 0 ||
        extraMovement.additional_items.length > 0)
        ? { ...extraMovement, id: 0 }
        : undefined;

    if (liquidationExtraMovement) {
      const liquidationExtraMovementContainers: IContainerAggregation[] = [];
      liquidationExtraMovement.articles.forEach((article) => {
        const articleContainer = article.article_locations.article.container;
        if (
          liquidationExtraMovementContainers.filter(
            (container) => container.container_id === articleContainer.id
          ).length === 0
        ) {
          liquidationExtraMovementContainers.push({
            container_id: articleContainer.id,
            description: articleContainer.description,
            movement_id: 0,
            m3_min: articleContainer.m3_min,
            m3_max: articleContainer.m3_max,
            volume_weight: articleContainer.volume_weight,
          });
        }
      });
      liquidationExtraMovement.containers = liquidationExtraMovementContainers;
    }

    const liquidationPayload = clientsToLiquidate.map((client) => {
      const originalTypes =
        selectedLiquidationTypesByClient[client.id] ??
        getSelectedLiquidationTypesDefaultValue();

      const isParent =
        hasChildren && selectedClient && client.id === selectedClient.id;

      const resolvedTypes = isParent
        ? buildZeroSettlementTypes(originalTypes, parentFallbackTypes)
        : originalTypes;

      return {
        ...liquidation,
        client_id: client.id,
        liquidation_settlement_types:
          mapSelectedLiquidationTypesToPayload(resolvedTypes),
        extra_movement:
          selectedClient && client.id === selectedClient.id
            ? liquidationExtraMovement
            : undefined,
      };
    });

    const response = await createLiquidationBatch(liquidationPayload, true, false);

    if (response.status === STATUS_CODES.ACCEPTED) {
      const batchId = Number(response.data?.batchId);

      if (!Number.isFinite(batchId)) {
        setLiquidationBatchProcessing(false);
        showSnackBar(
          "No se pudo obtener el lote de procesamiento de liquidacion.",
          "error",
        );
        return;
      }

      setLiquidationBatchProcessing(true);
      showSnackBar(
        response.data?.message ??
          "Lote de liquidacion recibido y en procesamiento.",
        "info",
      );
      void pollLiquidationBatch(batchId);
    }
  };

  const mapSelectedLiquidationTypesToPayload = (
    types: SelectedLiquidationTypes
  ) => {
    return [
     {
      settlement_type: types.income.description,
      calculation: parseFloat(types.income.calculation),
      minimum_value: parseFloat(types.income.minimum_value || "0"),
    },
    {
      settlement_type: types.stay.description,
      calculation: parseFloat(types.stay.calculation),
      minimum_value: parseFloat(types.stay.minimum_value || "0"),
    },
    {
      settlement_type: types.discharge.description,
      calculation: parseFloat(types.discharge.calculation),
      minimum_value: parseFloat(types.discharge.minimum_value || "0"),
    },
    ];
  };

  const canCreateLiquidation = useMemo(() => {
    if (!liquidation.client_id || !liquidation.date_from || !liquidation.date_to) {
      return false;
    }

    if (childClients.length > 0 && selectedChildClientIds.length === 0) {
      return false;
    }

    const hasChildren = clientsToLiquidate.length > 1;
    const parentFallbackTypes = resolveParentFallbackTypes();

    return clientsToLiquidate.every((client) => {
      const originalTypes =
        selectedLiquidationTypesByClient[client.id] ??
        getSelectedLiquidationTypesDefaultValue();
      const isParent =
        hasChildren && selectedClient && client.id === selectedClient.id;
      const types = isParent
        ? buildZeroSettlementTypes(originalTypes, parentFallbackTypes)
        : originalTypes;

      return (
        !!types.income.calculation &&
        !!types.stay.calculation &&
        !!types.discharge.calculation
      );
    });
  }, [
    liquidation.client_id,
    liquidation.date_from,
    liquidation.date_to,
    clientsToLiquidate,
    selectedClient,
    selectedLiquidationTypesByClient,
    childClients.length,
    selectedChildClientIds.length,
  ]);

  useEffect(() => {
    clientDefaultLiquidationTypes();
  }, [clientLiquidationTypes, resetLiquidationTypes]);

  useEffect(() => {
    return () => {
      clearBatchPolling();
    };
  }, [clearBatchPolling]);
  
  useEffect(() => {
    setSelectedLiquidationTypesByClient({});
    setSelectedChildClientIds([]);
  }, [liquidation.client_id]);

  useEffect(() => {
    setSelectedChildClientIds(childClients.map((client) => client.id));
  }, [childClients]);

  useEffect(() => {
    if (clientsToLiquidate.length === 0) return;

    setSelectedLiquidationTypesByClient((prev) => {
      const next: SelectedLiquidationTypesByClient = {};

      for (const client of clientsToLiquidate) {
        next[client.id] = prev[client.id] ?? getSelectedLiquidationTypesDefaultValue();
      }

      return next;
    });
  }, [clientsToLiquidate]);

  useEffect(() => {
    if (!selectedClient) return;

    setSelectedLiquidationTypesByClient((prev) => ({
      ...prev,
      [selectedClient.id]:
        prev[selectedClient.id] ?? selectedLiquidationTypes,
    }));
  }, [selectedClient, selectedLiquidationTypes]);

  useEffect(() => {
    if (clientsToLiquidate.length === 0) return;

    setLiquidationTypesLoading(true);

    setSelectedLiquidationTypesByClient((prev) => {
      const next = { ...prev };

      for (const client of clientsToLiquidate) {
        next[client.id] = buildDefaultLiquidationTypesFromClient(client.id);
      }

      return next;
    });

    setLiquidationTypesLoading(false);
  }, [clientsToLiquidate]);

  if (liquidationLoading || liquidationBatchProcessing) {
    return (
      <Grid item xs={12} display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Grid>
    );
  } else {
    return (
      <>
        {(liquidationLoading || liquidationBatchProcessing) && (
          <Grid item xs={12} display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Grid>
        )}
        <AdditionalsModal
          open={additionalsModalOpen}
          onClose={() => {
            setExtraMovement(undefined);
            setAdditionalsModalOpen(!additionalsModalOpen);
          }}
          liquidation={liquidation}
          onAdditionalItemChange={onAdditionalItemChange}
          onLiquidationCreate={handleCreateLiquidation}
          onMovementChange={handleMovementChange}
          extraMovement={extraMovement}
        />
        <Grid item xs={3}>
          <FormControl fullWidth margin="none">
            <Autocomplete
              id="user"
              autoComplete
              options={clients?.map((c) => ({
                id: c.id,
                label: `${c.code} - ${c.razon_social}`,
              }))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(
                e,
                value: {
                  id: number;
                  label: string;
                } | null
              ) => {
                handleChange("client_id", value?.id || null);
                resetLiquidationTypes();
              }}
              renderInput={(params) => (
                <TextField {...params} required label="Cliente" />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth margin="none">
            <Autocomplete
              id="warehouse"
              autoComplete
              options={storeOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(
                e,
                value: { id: number | null; label: string } | null
              ) => {
                handleChange("store_id", value?.id || null);
              }}
              fullWidth
              disabled={!liquidation.client_id}
              renderInput={(params) => (
                <TextField {...params} required label="Almacen" />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth margin="none">
            <DateTimePicker
              disableFuture
              label="Desde"
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{
                actionBar: DateTimePickerActionBar,
              }}
              slotProps={{
                textField: {
                  required: true,
                },
              }}
              onChange={(value) => {
                handleChange(
                  "date_from",
                  utcFormatter.parse(
                    dateISOFormat(value as unknown as string) as string
                  )
                );
              }}
              views={DATE_VIEWS}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth margin="none">
            <DateTimePicker
              minDate={liquidation.date_from && new Date(liquidation.date_from)}
              label="Hasta"
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{
                actionBar: DateTimePickerActionBar,
              }}
              slotProps={{
                textField: {
                  required: true,
                },
              }}
              views={DATE_VIEWS}
              onChange={(value) =>
                handleChange(
                  "date_to",
                  utcFormatter.parse(
                    dateISOFormat(value as unknown as string) as string
                  )
                )
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <TextField
              label="Observaciones"
              multiline
              minRows={3}
              value={liquidation.observations}
              onChange={(e) => handleChange("observations", e.target.value)}
            />
          </FormControl>
        </Grid>
        {childClients.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Líneas a liquidar
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {childClients.map((client) => (
                <FormControlLabel
                  key={client.id}
                  control={
                    <Checkbox
                      checked={selectedChildClientIds.includes(client.id)}
                      onChange={(e) => {
                        setSelectedChildClientIds((prev) =>
                          e.target.checked
                            ? [...prev, client.id]
                            : prev.filter((id) => id !== client.id)
                        );
                      }}
                    />
                  }
                  label={`${client.code} - ${client.razon_social}`}
                />
              ))}
            </Box>
          </Grid>
        )}
        {clientsToRender.map((client) => (
          <Grid item xs={12} key={client.id}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {client.parent_client_id
                ? `Línea: ${client.code} - ${client.razon_social}`
                : `Cliente Padre: ${client.code} - ${client.razon_social}`}
            </Typography>

            <LiquidationsTypesSelectors
              liquidation={{
                ...liquidation,
                client_id: client.id,
              }}
              liquidationTypesLoading={liquidationTypesLoading}
              selectedLiquidationTypes={
                selectedLiquidationTypesByClient[client.id] ??
                getSelectedLiquidationTypesDefaultValue()
              }
              setSelectedLiquidationTypes={(types) => {
                setSelectedLiquidationTypesByClient((prev) => {
                  const resolvedTypes =
                    typeof types === "function"
                      ? types(
                          prev[client.id] ?? getSelectedLiquidationTypesDefaultValue()
                        )
                      : types;

                  return {
                    ...prev,
                    [client.id]: resolvedTypes,
                  };
                });

                if (selectedClient?.id === client.id) {
                  const resolvedTypes =
                    typeof types === "function"
                      ? types(
                          selectedLiquidationTypesByClient[client.id] ??
                            getSelectedLiquidationTypesDefaultValue()
                        )
                      : types;

                  setSelectedLiquidationTypes(resolvedTypes);
                }
              }}
            />
          </Grid>
        ))}
        <Grid item xs={12} padding={2} display="flex">
          <Button
            variant="contained"
            onClick={() => setAdditionalsModalOpen(true)}
            disabled={!canCreateLiquidation}
          >
            Crear Liquidación
          </Button>
        </Grid>
      </>
    );
  }
}
